import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../Branding/Logo';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../utils/contactInfo';
import styles from './Auth.module.scss';

export const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user) {
      navigate('/app', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await signIn(email, password);
      if (result.error) {
        setError(result.error.message);
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* Top Fold: Hero + Login */}
      <div className={styles.loginSplitView}>
        <div className={styles.heroSection}>
          <div className={styles.logoContainer}>
            <Logo size="large" />
          </div>
          <h2 className={styles.tagline}>Visual Routines for Everyone</h2>
          <p className={styles.description}>
            Gamify the learning process with your kids, manage your family's time,
            and discover people with similar routines!
          </p>

          <div className={styles.features}>
            <div className={styles.feature}>
              <span>🎮</span> Gamified Learning
            </div>
            <div className={styles.feature}>
              <span>👨‍👩‍👧‍👦</span> Family Sync
            </div>
            <div className={styles.feature}>
              <span>🌍</span> Social Routines
            </div>
          </div>
        </div>

        <div className={styles.card}>
          <h3>Welcome Back!</h3>
          <p className={styles.subText}>Login to continue your time journey</p>

          {error && (
            <div
              className={styles.error}
              role="alert"
              aria-live="polite"
              id="login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.form} noValidate>
            <div className={styles.inputGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div className={styles.inputGroup}>
              <label htmlFor="password">Password</label>
              <div className={styles.passwordWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-describedby={error ? 'login-error' : undefined}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            <button type="submit" className={styles.button} disabled={isSubmitting}>
              {isSubmitting ? 'Logging in…' : 'Login to MyMinu'}
            </button>
          </form>

          <div className={styles.link}>
            New to MyMinu? <Link to="/register">Create an account</Link>
          </div>
          <div className={styles.link} style={{ marginTop: '0.5rem' }}>
            <Link to="/forgot-password" style={{ fontSize: '0.9rem' }}>Forgot Password?</Link>
          </div>
          <div className={styles.link} style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Need help? <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>
          </div>
        </div>
      </div>
    </div>
  );
};
